
import React, { useState } from 'react';

const ReachCalculator: React.FC = () => {
  // Adjustable Parameters
  const [clubFollowers, setClubFollowers] = useState(2000);
  const [totalMembers, setTotalMembers] = useState(750);
  const [participationRate, setParticipationRate] = useState(70);
  const [cpm, setCpm] = useState(20);
  
  // Algorithmic Constants (Assumptions)
  const OFFICIAL_VISIBILITY = 0.08;
  const PEER_VISIBILITY = 0.25;
  const AVG_FOLLOWERS_PER_MEMBER = 300;

  // Calculations
  const activeAmplifiers = Math.round(totalMembers * (participationRate / 100));
  const officialReach = Math.round(clubFollowers * OFFICIAL_VISIBILITY);
  const ecosystemReach = Math.round(activeAmplifiers * AVG_FOLLOWERS_PER_MEMBER * PEER_VISIBILITY);
  const totalReach = officialReach + ecosystemReach;
  
  // Multiplier logic
  const multiplierVal = officialReach > 0 ? (totalReach / officialReach) : 0;
  const multiplier = multiplierVal.toFixed(1);
  const mediaValue = (totalReach / 1000) * cpm;

  return (
    <div className="w-full max-w-7xl mx-auto bg-white rounded-[40px] p-10 shadow-2xl text-gray-900 overflow-hidden border border-gray-100">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
        
        {/* Left Side: Inputs */}
        <div className="lg:col-span-3 flex flex-col justify-between">
          <div className="space-y-8">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 mb-2">Simulation Parameters</p>
            
            <div className="space-y-10">
              {/* Parameter 1: Club Social Followers */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-sm font-bold text-gray-800 uppercase">Club social followers</p>
                  <p className="text-3xl font-black text-gray-900">{clubFollowers.toLocaleString()}</p>
                </div>
                <p className="text-[10px] text-gray-400 mb-3 leading-tight">
                  Followers of the club’s official social media accounts. Used to calculate baseline reach without community amplification.
                </p>
                <input 
                  type="range" min="0" max="10000" step="100"
                  value={clubFollowers} 
                  onChange={(e) => setClubFollowers(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-100 rounded-full appearance-none cursor-pointer accent-gray-400"
                />
              </div>

              {/* Parameter 2: Total Members */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-sm font-bold text-gray-800 uppercase">Total Members</p>
                  <p className="text-3xl font-black text-gray-900">{totalMembers.toLocaleString()}</p>
                </div>
                <input 
                  type="range" min="50" max="5000" step="50"
                  value={totalMembers} 
                  onChange={(e) => setTotalMembers(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-100 rounded-full appearance-none cursor-pointer accent-purple-600"
                />
              </div>

              {/* Parameter 3: Participation Rate */}
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-sm font-bold text-purple-600 uppercase">Participation rate</p>
                  <p className="text-3xl font-black text-purple-600">{participationRate}%</p>
                </div>
                <p className="text-[10px] text-gray-400 mb-3 leading-tight">
                  Percentage of members who take a low-effort action (share or amplify). Driven by deadlines and incentives.
                </p>
                <input 
                  type="range" min="5" max="100" step="5"
                  value={participationRate} 
                  onChange={(e) => setParticipationRate(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-100 rounded-full appearance-none cursor-pointer accent-purple-600"
                />
              </div>
            </div>

            <div className="mt-4 p-5 bg-purple-50 rounded-2xl border border-purple-100 flex justify-between items-center">
               <div className="flex flex-col">
                 <span className="text-xs font-bold text-purple-400 uppercase tracking-widest">Active Amplifiers</span>
                 <span className="text-[10px] text-purple-300 font-medium">Calculated from total members × participation rate</span>
               </div>
               <span className="text-2xl font-black text-purple-700">{activeAmplifiers.toLocaleString()} members</span>
            </div>
          </div>

          {/* Bottom stats - Left side part */}
          <div className="grid grid-cols-2 gap-8 border-t border-gray-100 pt-8 mt-6">
            <div className="flex flex-col justify-end h-20">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Official Reach</p>
              <p className="text-3xl font-black text-gray-300 italic">~{officialReach.toLocaleString()}</p>
            </div>
            <div className="flex flex-col justify-end h-20">
              <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mb-1">New Total Reach</p>
              <p className="text-3xl font-black text-purple-600 italic">~{totalReach.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Right Side: Result Card */}
        <div className="lg:col-span-2">
          <div className="h-full bg-gray-50 border border-gray-100 rounded-[40px] p-10 flex flex-col justify-between relative overflow-hidden">
            {/* Decal */}
            <div className="absolute -top-6 -right-6 text-[10rem] font-black text-gray-200/50 leading-none pointer-events-none">
              {multiplier}x
            </div>

            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-2 h-6 bg-emerald-500 rounded-full" />
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-600">Valuation Engine</p>
              </div>

              {/* Selected CPM & Its Slider */}
              <div className="mb-8">
                <div className="flex justify-between items-end mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Selected CPM</p>
                  <p className="text-4xl font-black text-gray-900 text-right">€{cpm.toFixed(2)}</p>
                </div>
                
                <div className="relative px-1 pt-2">
                  <input 
                    type="range" min="10" max="50" step="1"
                    value={cpm} 
                    onChange={(e) => setCpm(parseInt(e.target.value))}
                    className="w-full h-1 bg-gray-300 rounded-full appearance-none cursor-pointer accent-emerald-500"
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-[8px] font-black text-gray-300 uppercase tracking-tighter">Min €10</span>
                    <div className="flex flex-col items-center">
                       <span className="text-[8px] font-black text-emerald-600/60 uppercase tracking-tighter">● €20 (Benchmark)</span>
                    </div>
                    <span className="text-[8px] font-black text-gray-300 uppercase tracking-tighter">Max €50</span>
                  </div>
                </div>
              </div>

              <div className="h-px bg-gray-200 w-full mb-8" />

              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-widest text-purple-500 mb-1">Estimated campaign media value</p>
                <p className="text-7xl font-black text-gray-900 tracking-tighter leading-none">
                  €{Math.round(mediaValue).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Bottom stats - Right side part (Multiplier) */}
            <div className="mt-8 pt-6 border-t border-gray-200/50 relative z-10 flex flex-col justify-end h-20">
               <div className="flex flex-col mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Reach multiplier</p>
                  <p className="text-[10px] text-gray-400 leading-tight">Compared to posting only on the club’s own channels</p>
               </div>
               <div className="flex justify-between items-center bg-white p-5 rounded-2xl border border-gray-200 shadow-sm w-full">
                  <span className="text-xs font-black text-purple-600 uppercase tracking-widest">Multiplier</span>
                  <span className="text-4xl font-black text-gray-900">{multiplier}x</span>
               </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Dynamic Math Verification Footer - Wider and softer colors */}
      <div className="mt-12 text-center border-t border-gray-100 pt-10">
        <div className="w-full mx-auto px-10 py-8 bg-indigo-50/40 backdrop-blur-sm rounded-3xl border border-indigo-100/50 text-left shadow-sm">
          <p className="text-[11px] font-black text-indigo-300 uppercase tracking-[0.4em] mb-4">Structural Verification Mechanism</p>
          <div className="text-base font-mono text-slate-700 flex flex-col gap-3">
            <div className="flex justify-between items-center pb-3 border-b border-indigo-100/50">
               <p><span className="font-bold">Official Club Channel:</span> {clubFollowers.toLocaleString()} followers × <span className="text-slate-500">8% organic reach</span></p>
               <p className="font-bold text-slate-400">~{officialReach.toLocaleString()} impressions</p>
            </div>
            
            <div className="flex justify-between items-center pt-2">
               <p className="text-emerald-600 font-bold">Community-Powered Amplification:</p>
               <p className="font-bold text-emerald-600">+{ecosystemReach.toLocaleString()} impressions</p>
            </div>
            
            <div className="pl-6 border-l-2 border-emerald-100 py-1">
              <p className="text-[12px] text-slate-400 leading-normal">
                Based on <span className="font-bold text-slate-500">{activeAmplifiers.toLocaleString()}</span> active members 
                averaging <span className="font-bold text-slate-500">{AVG_FOLLOWERS_PER_MEMBER}</span> followers each, 
                with <span className="font-bold text-slate-500">{PEER_VISIBILITY * 100}%</span> visibility for peer-to-peer posts.
              </p>
            </div>

            <div className="mt-4 pt-4 border-t-2 border-indigo-200 flex justify-between items-center">
              <span className="font-black text-purple-600 uppercase tracking-[0.2em] text-sm">Total Campaign Impressions</span>
              <span className="text-3xl font-black text-purple-800">~{totalReach.toLocaleString()} Reach</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReachCalculator;
